import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, writeBatch, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useModuleAdmin } from '../useModuleAdmin';
import { ACTOR_BUCKET_FOR, normaliseRole, isAdminRole } from '../roles';
import {
  Users, MapPin, Heart, BarChart3, Settings as SettingsIcon,
  Plus, Trash2, X, Upload, Download, ChevronDown, Wand2,
} from 'lucide-react';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const pad2 = (n) => String(n).padStart(2, '0');

// Mirrors Invigilation.jsx's actorRoleBucket -- Admin is its own bucket (always granted via the
// isAdmin safety net in canManage), Student is its own non-grantable bucket so a "Staff" grant can
// never accidentally cover students.
// Maps a canonical role onto the permission-checkbox labels, which are stored in customer data and
// so are not migrated. Anything absent from the map -- parent, denied -- matches no permission list,
// which is exactly the intent: they hold no duty permissions of any kind.
const actorRoleBucket = (rawRole) => ACTOR_BUCKET_FOR[normaliseRole(rawRole)] || 'Denied';

const timeToMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// <input type="time"> requires strictly zero-padded "HH:MM" -- anything else silently renders blank.
const normalizeTime = (hhmm) => {
  const parts = (hhmm || '').trim().split(':');
  if (parts.length < 2) return hhmm;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return `${pad2(h)}:${pad2(m)}`;
};

// Stable id for a patrol session definition -- patrol_assignment/patrol_preference reference sessions
// by this id, so it must survive the session being relabeled or reordered later.
const genSessionId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// Sticky sidebar of draggable staff initials, dropped onto an Allocation cell to assign that person.
// Same shape as Invigilation.jsx's StaffPanel (duplicated here since components aren't shared across
// module files in this codebase).
const StaffPanel = ({ staffList, onStaffDragStart, collapsed, onToggle }) => {
  const { t } = useT();
  return (
  <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', padding: '0.85rem' }}>
    <button
      type="button" onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: collapsed ? 0 : '0.6rem' }}
    >
      <ChevronDown size={14} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--color-text-muted)', flexShrink: 0 }} />
      <span style={{ fontSize: '0.8rem', fontWeight: '700' }}>{t('patrolDuty.staff')}</span>
    </button>
    {!collapsed && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {staffList.length === 0 ? (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.noEligibleStaffPanel')}</span>
        ) : staffList.map(st => (
          <div
            key={st.initial} draggable onDragStart={(e) => onStaffDragStart(e, st.initial)} title={st.name}
            style={{ cursor: 'grab', padding: '0.3rem 0.65rem', borderRadius: '999px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', fontSize: '0.75rem', fontWeight: '600' }}
          >
            {st.initial}
          </div>
        ))}
      </div>
    )}
  </div>
  );
};

const PatrolDuty = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile, isMobile } = useOutletContext();
  const [activeTab, setActiveTab] = useState('patrol');

  const ownRole = (userProfile?.role || '').toLowerCase();
  const isAdmin = isAdminRole(ownRole);
  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownActorRole = actorRoleBucket(ownRole);
  // Named admins of this module: the one list the rules read, replacing the per-action
  // staff pickers that used to live in this module's own settings document.
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'patrol_duty');

  // --- Academic years & staff ---
  const [academicYears, setAcademicYears] = useState([]);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    if (!activeSchoolId) return;
    (async () => {
      try {
        const ySnap = await getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
      } catch (e) { console.error('Error loading academic years', e); }

      try {
        const staffSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const list = [];
        staffSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          list.push({ email: d.id, initial: data.initial, name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial, role: (data.role || 'staff').toLowerCase() });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setStaffList(list);
      } catch (e) { console.error('Error loading staff list', e); }
    })();
  }, [activeSchoolId]);

  const staffMap = useMemo(() => Object.fromEntries(staffList.map(s => [s.initial, s])), [staffList]);

  // --- Settings: module admin, eligibility, sessions, patrol days, patrol year ---
  const [editPermissions, setEditPermissions] = useState({ roles: [] });
  // Who needs to patrol: (role match OR explicit include) MINUS explicit exclude. Nothing configured
  // (no roles, no include) means every staff member is eligible by default.
  const [patrolEligibility, setPatrolEligibility] = useState({ roles: [], staff: [], excludeStaff: [] });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // yearId/sessions/extraDays/excludedDays are edited as drafts and only take effect on Save -- every
  // other tab (Patrol/Allocation/Preference/Statistics) reads the *applied* state, not the draft, so
  // mid-edit typing doesn't reshuffle what everyone else sees (same reasoning as Invigilation's
  // examPeriods/currentAssessment draft split).
  const [yearId, setYearId] = useState('');
  const [yearDraft, setYearDraft] = useState('');
  const [sessions, setSessions] = useState([]); // [{ id, label, startTime, endTime, load }]
  const [sessionsDraft, setSessionsDraft] = useState([]);
  const [extraDays, setExtraDays] = useState([]);
  const [extraDaysDraft, setExtraDaysDraft] = useState([]);
  const [excludedDays, setExcludedDays] = useState([]);
  const [excludedDaysDraft, setExcludedDaysDraft] = useState([]);

  const canManage = isAdmin || isModuleAdmin || editPermissions.roles.includes(ownActorRole);

  useEffect(() => {
    if (!activeSchoolId) return;
    setSettingsLoaded(false);
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'patrol_settings'))
      .then(snap => {
        const data = snap.exists() ? snap.data() : {};
        setEditPermissions({ roles: data.editPermissions?.roles || [] });
        setPatrolEligibility({
          roles: data.patrolEligibility?.roles || [],
          staff: data.patrolEligibility?.staff || [],
          excludeStaff: data.patrolEligibility?.excludeStaff || [],
        });
        setYearId(data.yearId || '');
        setYearDraft(data.yearId || '');
        setSessions(data.sessions || []);
        setSessionsDraft(data.sessions || []);
        setExtraDays(data.extraDays || []);
        setExtraDaysDraft(data.extraDays || []);
        setExcludedDays(data.excludedDays || []);
        setExcludedDaysDraft(data.excludedDays || []);
      })
      .catch(e => console.error('Error loading patrol settings', e))
      .finally(() => setSettingsLoaded(true));
  }, [activeSchoolId]);

  // Default the patrol year to the current academic year the first time settings load with nothing
  // saved yet -- once a year is ever saved, this never overrides it again.
  useEffect(() => {
    if (!settingsLoaded || yearId || academicYears.length === 0) return;
    const cur = academicYears.find(y => y.cur_year) || academicYears[academicYears.length - 1];
    setYearId(cur.yearId);
    setYearDraft(cur.yearId);
  }, [settingsLoaded, academicYears, yearId]);

  const sessionMap = useMemo(() => Object.fromEntries(sessions.map(s => [s.id, s])), [sessions]);

  const eligibleStaffList = useMemo(() => {
    const base = (patrolEligibility.roles.length === 0 && patrolEligibility.staff.length === 0)
      ? staffList
      : staffList.filter(s => patrolEligibility.roles.includes(actorRoleBucket(s.role)) || patrolEligibility.staff.includes(s.email));
    return base.filter(s => !patrolEligibility.excludeStaff.includes(s.email));
  }, [staffList, patrolEligibility]);

  // --- School Calendar: which dates are "School Day" for the applied patrol year ---
  const [calendarEvents, setCalendarEvents] = useState([]);
  useEffect(() => {
    if (!activeSchoolId || !yearId) { setCalendarEvents([]); return; }
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events'), where('yearId', '==', yearId)));
        const list = [];
        snap.forEach(d => list.push(d.data()));
        setCalendarEvents(list);
      } catch (e) { console.error('Error loading school calendar', e); }
    })();
  }, [activeSchoolId, yearId]);

  const schoolDays = useMemo(() => calendarEvents.filter(e => e.type === 'School Day').map(e => e.date), [calendarEvents]);

  // Patrol days = school days, plus admin-added extra days, minus admin-excluded school days.
  const patrolDays = useMemo(() => {
    const set = new Set([...schoolDays, ...extraDays]);
    excludedDays.forEach(d => set.delete(d));
    return [...set].sort();
  }, [schoolDays, extraDays, excludedDays]);

  // --- Assignments: one doc per (date, session), id = `${date}__${sessionId}` ---
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  useEffect(() => {
    if (!activeSchoolId || !yearId) { setAssignments([]); return; }
    setLoadingAssignments(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'patrol_assignment'), where('yearId', '==', yearId));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setAssignments(list);
      setLoadingAssignments(false);
    }, (e) => { console.error('Error loading patrol assignments', e); setLoadingAssignments(false); });
    return unsub;
  }, [activeSchoolId, yearId]);

  const assignmentMap = useMemo(() => {
    const map = {};
    assignments.forEach(a => { map[`${a.date}__${a.sessionId}`] = a.staff || []; });
    return map;
  }, [assignments]);

  // --- Preferences: one doc per staff initial, live across the whole module (needed by both the
  // Preference tab and Allocation's auto-assign) ---
  const [preferences, setPreferences] = useState({});
  useEffect(() => {
    if (!activeSchoolId) { setPreferences({}); return; }
    const unsub = onSnapshot(collection(db, 'schools', activeSchoolId, 'patrol_preference'), (snap) => {
      const map = {};
      // Keyed by initial for this screen, which is a grid of initials. The DOCUMENT is keyed by
      // address -- that is what lets the rule prove a preference is yours without a lookup -- so the
      // initial is read from the body. Falling back to the id keeps pre-migration documents
      // readable, so the two can be deployed in either order without the grid going blank.
      snap.forEach(d => {
        const data = d.data() || {};
        map[data.initial || d.id] = data.sessionIds || [];
      });
      setPreferences(map);
    }, (e) => console.error('Error loading patrol preferences', e));
    return unsub;
  }, [activeSchoolId]);

  const togglePreference = async (initial, sessionId) => {
    const current = preferences[initial] || [];
    const next = current.includes(sessionId) ? current.filter(s => s !== sessionId) : [...current, sessionId];
    // The rule is `document id == your address`, so an initial that resolves to nobody has no
    // document it is allowed to write. Said out loud rather than attempted and left to fail as a
    // permission error, which reads like the app is broken rather than the directory being short a
    // row.
    const owner = staffList.find(st => st.initial === initial);
    if (!owner?.email) {
      alert(t('patrolDuty.noProfile', { initial }));
      return;
    }
    try {
      await setDoc(
        doc(db, 'schools', activeSchoolId, 'patrol_preference', owner.email),
        { sessionIds: next, initial, staffEmail: owner.email },
        { merge: true },
      );
    } catch (e) { console.error('Error updating patrol preference', e); alert(t('patrolDuty.prefFailed', { message: e.message })); }
  };

  // --- Statistics matrix: total load per staff per session, across the whole applied patrol year ---
  const statsMatrix = useMemo(() => {
    const m = {};
    assignments.forEach(a => {
      const sess = sessionMap[a.sessionId];
      const load = sess ? (Number(sess.load) || 0) : 0;
      (a.staff || []).forEach(initial => {
        if (!m[initial]) m[initial] = {};
        m[initial][a.sessionId] = (m[initial][a.sessionId] || 0) + load;
      });
    });
    return m;
  }, [assignments, sessionMap]);

  // Row list for both Statistics and Preference tabs: every eligible staff member, plus anyone with
  // recorded history (assignment or preference) even if no longer eligible today.
  const patrolStaffList = useMemo(() => {
    const byInitial = new Map();
    eligibleStaffList.forEach(s => byInitial.set(s.initial, s));
    staffList.forEach(s => { if (statsMatrix[s.initial] || preferences[s.initial]) byInitial.set(s.initial, s); });
    return [...byInitial.values()].sort((a, b) => a.initial.localeCompare(b.initial));
  }, [eligibleStaffList, staffList, statsMatrix, preferences]);

  // --- Assign / remove staff on an Allocation cell ---
  const assignStaff = async (date, sessionId, initial) => {
    const key = `${date}__${sessionId}`;
    const current = assignmentMap[key] || [];
    if (current.includes(initial)) return;
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'patrol_assignment', key), {
        yearId, date, sessionId, staff: [...current, initial],
      }, { merge: true });
    } catch (e) { console.error('Error assigning patrol staff', e); alert(t('patrolDuty.assignFailed', { message: e.message })); }
  };

  const removeStaff = async (date, sessionId, initial) => {
    const key = `${date}__${sessionId}`;
    const current = assignmentMap[key] || [];
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'patrol_assignment', key), {
        yearId, date, sessionId, staff: current.filter(x => x !== initial),
      }, { merge: true });
    } catch (e) { console.error('Error removing patrol staff', e); alert(t('patrolDuty.removeFailed', { message: e.message })); }
  };

  // --- Clash detection: same soft (highlight, not block) validation as Invigilation -- flags a staff
  // chip red if that same initial is on another session the same day whose time range overlaps ---
  const clashesByDate = useMemo(() => {
    const clashes = new Map(); // key `${date}__${sessionId}` -> Set(initial)
    const flag = (key, initial) => {
      if (!clashes.has(key)) clashes.set(key, new Set());
      clashes.get(key).add(initial);
    };
    const byDate = {};
    assignments.forEach(a => {
      if (!(a.staff || []).length) return;
      if (!byDate[a.date]) byDate[a.date] = [];
      byDate[a.date].push(a);
    });
    Object.values(byDate).forEach(dayAssignments => {
      for (let i = 0; i < dayAssignments.length; i++) {
        const a = dayAssignments[i];
        const sessA = sessionMap[a.sessionId];
        if (!sessA) continue;
        const aStart = timeToMinutes(sessA.startTime);
        const aEnd = timeToMinutes(sessA.endTime);
        if (aStart == null || aEnd == null) continue;
        for (let j = i + 1; j < dayAssignments.length; j++) {
          const b = dayAssignments[j];
          if (a.sessionId === b.sessionId) continue;
          const sessB = sessionMap[b.sessionId];
          if (!sessB) continue;
          const bStart = timeToMinutes(sessB.startTime);
          const bEnd = timeToMinutes(sessB.endTime);
          if (bStart == null || bEnd == null) continue;
          if (!(aStart < bEnd && bStart < aEnd)) continue;
          (a.staff || []).forEach(initial => {
            if ((b.staff || []).includes(initial)) {
              flag(`${a.date}__${a.sessionId}`, initial);
              flag(`${b.date}__${b.sessionId}`, initial);
            }
          });
        }
      }
    });
    return clashes;
  }, [assignments, sessionMap]);

  const isClashing = (date, sessionId, initial) => clashesByDate.get(`${date}__${sessionId}`)?.has(initial) || false;

  // --- Auto Assign: fills every empty (date, session) slot across the whole patrol year, preferring
  // staff who marked that session as preferred, and among those (or among everyone eligible and free
  // if nobody preferring is available) picking whoever has the least total load so far, so time ends
  // up balanced. A candidate is skipped for a slot if it would double-book them against another
  // session the same day whose time overlaps -- clash-free by construction. Slots that already have
  // at least one staff member are left untouched. ---
  const [autoAssigning, setAutoAssigning] = useState(false);
  const handleAutoAssignPatrol = async () => {
    if (eligibleStaffList.length === 0) return alert(t('patrolDuty.noEligibleForAuto'));
    if (sessions.length === 0) return alert(t('patrolDuty.noSessionsForAuto'));
    if (patrolDays.length === 0) return alert(t('patrolDuty.noDaysForAuto'));

    const targets = [];
    patrolDays.forEach(date => {
      sessions.forEach(sess => {
        const key = `${date}__${sess.id}`;
        if ((assignmentMap[key] || []).length === 0) targets.push({ date, session: sess, key });
      });
    });
    if (targets.length === 0) return alert(t('patrolDuty.allSlotsFilled'));
    if (!window.confirm(t('patrolDuty.autoAssignConfirm', { count: targets.length }))) return;

    setAutoAssigning(true);
    try {
      const busy = {}; // initial -> [{date,start,end}]
      const totalLoad = {}; // initial -> number
      eligibleStaffList.forEach(s => { totalLoad[s.initial] = 0; });
      assignments.forEach(a => {
        const sess = sessionMap[a.sessionId];
        if (!sess) return;
        const start = timeToMinutes(sess.startTime);
        const end = timeToMinutes(sess.endTime);
        (a.staff || []).forEach(initial => {
          if (start != null && end != null) {
            if (!busy[initial]) busy[initial] = [];
            busy[initial].push({ date: a.date, start, end });
          }
          totalLoad[initial] = (totalLoad[initial] || 0) + (Number(sess.load) || 0);
        });
      });

      const sessionOrder = sessions.map(s => s.id);
      const sortedTargets = [...targets].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return sessionOrder.indexOf(a.session.id) - sessionOrder.indexOf(b.session.id);
      });
      const staffOrder = [...eligibleStaffList].sort((a, b) => a.initial.localeCompare(b.initial));

      const updates = []; // { key, date, sessionId, initial }
      const unassigned = [];

      sortedTargets.forEach(({ date, session: sess, key }) => {
        const start = timeToMinutes(sess.startTime);
        const end = timeToMinutes(sess.endTime);
        const candidates = staffOrder.filter(st => {
          if (start == null || end == null) return true;
          return !(busy[st.initial] || []).some(b => b.date === date && start < b.end && b.start < end);
        });
        if (candidates.length === 0) { unassigned.push({ date, session: sess }); return; }
        const preferring = candidates.filter(st => (preferences[st.initial] || []).includes(sess.id));
        const pool = preferring.length > 0 ? preferring : candidates;
        let best = null;
        pool.forEach(st => {
          const load = totalLoad[st.initial] || 0;
          if (!best || load < best.load) best = { initial: st.initial, load };
        });
        updates.push({ key, date, sessionId: sess.id, initial: best.initial });
        if (start != null && end != null) {
          if (!busy[best.initial]) busy[best.initial] = [];
          busy[best.initial].push({ date, start, end });
        }
        totalLoad[best.initial] = (totalLoad[best.initial] || 0) + (Number(sess.load) || 0);
      });

      let batch = writeBatch(db);
      let batchCount = 0;
      for (const u of updates) {
        batch.set(doc(db, 'schools', activeSchoolId, 'patrol_assignment', u.key), { yearId, date: u.date, sessionId: u.sessionId, staff: [u.initial] }, { merge: true });
        batchCount++;
        if (batchCount === 500) { await batch.commit(); batch = writeBatch(db); batchCount = 0; }
      }
      if (batchCount > 0) await batch.commit();

      alert(
        `Auto-assign complete: ${updates.length} slot(s) assigned.` +
        (unassigned.length ? ` ${unassigned.length} slot(s) could not be assigned -- no eligible staff free at that time.` : '')
      );
    } catch (e) {
      console.error('Error auto-assigning patrol', e);
      alert(t('patrolDuty.autoAssignFailed', { message: e.message }));
    }
    setAutoAssigning(false);
  };

  // --- Preference CSV import/export ---
  const [importingPreference, setImportingPreference] = useState(false);
  const [exportingPreference, setExportingPreference] = useState(false);
  const preferenceFileInputRef = useRef(null);

  const handleExportPreferenceCSV = () => {
    setExportingPreference(true);
    try {
      const csvData = patrolStaffList.map(s => ({
        'Staff Initial': s.initial,
        'Preferred Sessions': (preferences[s.initial] || []).join(';'),
      }));
      if (csvData.length === 0) csvData.push({ 'Staff Initial': '', 'Preferred Sessions': '' });
      const csv = Papa.unparse(csvData);
      const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'patrol_preferences_export.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting patrol preferences', e);
      alert(t('patrolDuty.exportFailed', { message: e.message }));
    }
    setExportingPreference(false);
  };

  const handleImportPreferenceFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportingPreference(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let updated = 0, skipped = 0;
        try {
          let batch = writeBatch(db);
          let batchCount = 0;
          const commitBatch = async () => {
            if (batchCount === 0) return;
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          };
          for (const r of (results.data || [])) {
            const initial = (r['Staff Initial'] || '').trim();
            if (!initial || !staffMap[initial]) { skipped++; continue; }
            const raw = (r['Preferred Sessions'] || '').trim();
            const sessionIds = raw ? raw.split(';').map(s => s.trim()).filter(Boolean) : [];
            batch.set(doc(db, 'schools', activeSchoolId, 'patrol_preference', initial), { sessionIds }, { merge: true });
            batchCount++;
            updated++;
            if (batchCount === 500) await commitBatch();
          }
          await commitBatch();
          alert(skipped
            ? t('patrolDuty.importDoneSkipped', { updated, skipped })
            : t('patrolDuty.importDone', { updated }));
        } catch (err) {
          console.error('Error importing patrol preferences', err);
          alert(t('patrolDuty.importFailed', { message: err.message }));
        }
        setImportingPreference(false);
      },
      error: (err) => { setImportingPreference(false); alert(t('patrolDuty.parseFailed', { message: err.message })); },
    });
  };

  // --- Settings: module admin / eligibility toggles (mutate live, committed to Firestore on Save) ---
  const toggleEditRole = (role) => setEditPermissions(prev => ({
    ...prev, roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
  }));
  const toggleEligibleRole = (role) => setPatrolEligibility(prev => ({
    ...prev, roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
  }));
  const toggleIncludeStaff = (email) => setPatrolEligibility(prev => ({
    ...prev, staff: prev.staff.includes(email) ? prev.staff.filter(e => e !== email) : [...prev.staff, email],
  }));
  const toggleExcludeStaff = (email) => setPatrolEligibility(prev => ({
    ...prev, excludeStaff: prev.excludeStaff.includes(email) ? prev.excludeStaff.filter(e => e !== email) : [...prev.excludeStaff, email],
  }));

  const [showIncludeModal, setShowIncludeModal] = useState(false);
  const [includeSearch, setIncludeSearch] = useState('');
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [excludeSearch, setExcludeSearch] = useState('');

  // --- Settings: sessions draft CRUD ---
  const addSessionRow = () => setSessionsDraft(prev => [...prev, { id: genSessionId(), label: `Session ${prev.length + 1}`, startTime: '08:00', endTime: '08:30', load: 1 }]);
  const updateSessionRow = (id, field, value) => setSessionsDraft(prev => prev.map(s => (s.id === id ? { ...s, [field]: value } : s)));
  const removeSessionRow = (id) => setSessionsDraft(prev => prev.filter(s => s.id !== id));

  // --- Settings: extra/excluded days draft CRUD ---
  const [newExtraDay, setNewExtraDay] = useState('');
  const [newExcludedDay, setNewExcludedDay] = useState('');
  const addExtraDay = () => {
    if (!newExtraDay) return;
    setExtraDaysDraft(prev => (prev.includes(newExtraDay) ? prev : [...prev, newExtraDay].sort()));
    setNewExtraDay('');
  };
  const removeExtraDay = (d) => setExtraDaysDraft(prev => prev.filter(x => x !== d));
  const addExcludedDay = () => {
    if (!newExcludedDay) return;
    setExcludedDaysDraft(prev => (prev.includes(newExcludedDay) ? prev : [...prev, newExcludedDay].sort()));
    setNewExcludedDay('');
  };
  const removeExcludedDay = (d) => setExcludedDaysDraft(prev => prev.filter(x => x !== d));

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const cleanSessions = sessionsDraft.map(s => ({
        id: s.id, label: (s.label || '').trim() || 'Session', startTime: normalizeTime(s.startTime), endTime: normalizeTime(s.endTime), load: Number(s.load) || 0,
      }));
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'patrol_settings'), {
        editPermissions, patrolEligibility,
        yearId: yearDraft, sessions: cleanSessions, extraDays: extraDaysDraft, excludedDays: excludedDaysDraft,
      }, { merge: true });
      setYearId(yearDraft);
      setSessions(cleanSessions);
      setSessionsDraft(cleanSessions);
      setExtraDays(extraDaysDraft);
      setExcludedDays(excludedDaysDraft);
    } catch (e) {
      console.error('Error saving patrol settings', e);
      alert(t('patrolDuty.settingsFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // --- Allocation tab: drag handlers + month filter ---
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const handleStaffDragStart = (e, initial) => { e.dataTransfer.setData('application/x-patrol-staff-initial', initial); };

  const [monthFilter, setMonthFilter] = useState('');
  const monthOptions = useMemo(() => [...new Set(patrolDays.map(d => d.slice(0, 7)))].sort(), [patrolDays]);
  useEffect(() => {
    if (monthOptions.length === 0) { setMonthFilter(''); return; }
    if (monthOptions.includes(monthFilter)) return;
    const todayMonth = new Date().toISOString().slice(0, 7);
    setMonthFilter(monthOptions.find(m => m >= todayMonth) || monthOptions[0]);
  }, [monthOptions, monthFilter]);

  const filteredPatrolDays = useMemo(() => patrolDays.filter(d => d.slice(0, 7) === monthFilter), [patrolDays, monthFilter]);

  // Patrol tab groups every patrol day into its month -- each group renders as a day-rows x
  // session-columns table, mirroring the Allocation tab's grid but read-only.
  const patrolByMonth = useMemo(() => {
    const map = {};
    patrolDays.forEach(d => {
      const month = d.slice(0, 7);
      if (!map[month]) map[month] = [];
      map[month].push(d);
    });
    return monthOptions.map(month => [month, map[month] || []]);
  }, [patrolDays, monthOptions]);

  const formatMonth = (m) => {
    const dt = new Date(`${m}-01T00:00:00`);
    if (Number.isNaN(dt.getTime())) return m;
    return dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Only the first 5 months are expanded by default on the Patrol tab -- a full year's worth of days
  // would otherwise all render open at once. Never overrides a manual toggle already made.
  useEffect(() => {
    if (monthOptions.length <= 5) return;
    setCollapsedSections(prev => {
      let changed = false;
      const next = { ...prev };
      monthOptions.forEach((month, i) => {
        const key = `patrolMonth:${month}`;
        if (i >= 5 && !(key in next)) { next[key] = true; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [monthOptions]);

  const formatDate = (d) => {
    const dt = new Date(`${d}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;
    return `${d} (${dt.toLocaleDateString('en-US', { weekday: 'short' })})`;
  };

  const tabs = [
    { id: 'patrol', label: t('patrolDuty.tabs.patrol'), icon: MapPin },
    { id: 'allocation', label: t('patrolDuty.tabs.allocation'), icon: Users },
    { id: 'preference', label: t('patrolDuty.tabs.preference'), icon: Heart },
    { id: 'statistics', label: t('patrolDuty.tabs.statistics'), icon: BarChart3 },
    { id: 'settings', label: t('patrolDuty.tabs.settings'), icon: SettingsIcon },
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${isActive ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {activeTab === 'allocation' && (
            <select className="input-field" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} style={{ width: '150px' }}>
              {monthOptions.length === 0 ? <option value="">-</option> : monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
            Year: {yearId || '-'}
          </div>
          {activeTab === 'preference' && canManage && (
            <>
              <input type="file" ref={preferenceFileInputRef} accept=".csv" style={{ display: 'none' }} onChange={handleImportPreferenceFile} />
              <button className="btn-outline" onClick={() => preferenceFileInputRef.current?.click()} disabled={importingPreference} title={t('patrolDuty.importPrefs')} style={{ width: 'auto', padding: '0.6rem', display: 'flex', alignItems: 'center' }}>
                <Upload size={16} />
              </button>
              <button className="btn-outline" onClick={handleExportPreferenceCSV} disabled={exportingPreference} title={t('patrolDuty.exportPrefs')} style={{ width: 'auto', padding: '0.6rem', display: 'flex', alignItems: 'center' }}>
                <Download size={16} />
              </button>
            </>
          )}
          {activeTab === 'allocation' && canManage && (
            <button
              className="btn-primary" onClick={handleAutoAssignPatrol} disabled={autoAssigning}
              title={t('patrolDuty.autoAssignTitle')}
              style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}
            >
              <Wand2 size={16} /> {autoAssigning ? t('patrolDuty.assigning') : t('patrolDuty.autoAssign')}
            </button>
          )}
          {activeTab === 'settings' && (
            <button className="btn-primary" onClick={handleSaveSettings} disabled={!canManage || savingSettings} style={{ width: 'auto', padding: '0.5rem 1.25rem' }}>
              {savingSettings ? t('common.saving') : t('common.save')}
            </button>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content -- outer unpadded scrollable div + inner padded div, same split used in
          Invigilation.jsx so the vertical scrollbar doesn't eat into content width vs. the tabs bar. */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: '0 1.5rem 1.5rem' }}>

        {activeTab === 'patrol' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {patrolByMonth.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>
                No patrol days configured for {yearId || 'this year'} yet.
              </div>
            ) : patrolByMonth.map(([month, dates]) => {
              const key = `patrolMonth:${month}`;
              const collapsed = !!collapsedSections[key];
              return (
                <div key={month} className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
                  <button
                    type="button" onClick={() => toggleSection(key)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0.75rem 1rem', borderBottom: collapsed ? 'none' : '1px solid var(--color-border)', textAlign: 'left' }}
                  >
                    <ChevronDown size={16} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--color-text-muted)', flexShrink: 0 }} />
                    <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>{formatMonth(month)}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>({dates.length} day{dates.length === 1 ? '' : 's'})</span>
                  </button>
                  {!collapsed && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('patrolDuty.date')}</th>
                            {sessions.map(sess => (
                              <th key={sess.id} style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{sess.label}<div style={{ fontWeight: '400' }}>{sess.startTime} - {sess.endTime}</div></th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sessions.length === 0 ? (
                            <tr><td colSpan={1} style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.noSessionsConfigured')}</td></tr>
                          ) : dates.map((date, i) => (
                            <tr key={date} style={{ borderBottom: i === dates.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: '600', whiteSpace: 'nowrap' }}>{formatDate(date)}</td>
                              {sessions.map(sess => {
                                const staff = assignmentMap[`${date}__${sess.id}`] || [];
                                return (
                                  <td key={sess.id} style={{ padding: '0.75rem 1rem' }}>
                                    {staff.length === 0 ? (
                                      <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                                    ) : (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                                        {staff.map(initial => (
                                          <span key={initial} style={{ padding: '0.2rem 0.55rem', borderRadius: '999px', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', fontSize: '0.75rem', fontWeight: '600' }}>
                                            {initial}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'allocation' && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <div style={{ width: '200px', flexShrink: 0, position: 'sticky', top: 0 }}>
              <StaffPanel
                staffList={eligibleStaffList} onStaffDragStart={handleStaffDragStart}
                collapsed={!!collapsedSections['panel:staff']} onToggle={() => toggleSection('panel:staff')}
              />
            </div>
            <div className="table-container" style={{ flex: 1, minWidth: 0, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              {sessions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.noSessionsForAuto')}</div>
              ) : filteredPatrolDays.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.noPatrolDays')}</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                      <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('patrolDuty.date')}</th>
                      {sessions.map(sess => (
                        <th key={sess.id} style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{sess.label}<div style={{ fontWeight: '400' }}>{sess.startTime} - {sess.endTime}</div></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatrolDays.map((date, i) => (
                      <tr key={date} style={{ borderBottom: i === filteredPatrolDays.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '600', whiteSpace: 'nowrap' }}>{formatDate(date)}</td>
                        {sessions.map(sess => {
                          const key = `${date}__${sess.id}`;
                          const staff = assignmentMap[key] || [];
                          return (
                            <td
                              key={sess.id} style={{ padding: '0.5rem', minWidth: '140px', verticalAlign: 'top' }}
                              onDragOver={canManage ? (e) => e.preventDefault() : undefined}
                              onDrop={canManage ? (e) => {
                                const initial = e.dataTransfer.getData('application/x-patrol-staff-initial');
                                if (initial) assignStaff(date, sess.id, initial);
                              } : undefined}
                            >
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', minHeight: '28px', border: '1px dashed var(--color-border)', borderRadius: 'var(--radius-md)', padding: '0.35rem' }}>
                                {staff.map(initial => {
                                  const clashing = isClashing(date, sess.id, initial);
                                  return (
                                    <span
                                      key={initial}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600',
                                        backgroundColor: clashing ? 'var(--color-danger-bg)' : 'var(--color-bg-app)',
                                        color: clashing ? 'var(--color-danger)' : 'var(--color-text-main)',
                                        border: clashing ? '1px solid var(--color-danger)' : '1px solid var(--color-border)',
                                      }}
                                    >
                                      {initial}
                                      {canManage && (
                                        <button type="button" onClick={() => removeStaff(date, sess.id, initial)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: 'inherit', padding: 0 }}>
                                          <X size={11} />
                                        </button>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'preference' && (
          <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)' }}>
                {t('patrolDuty.readOnlyPrefs')}
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('patrolDuty.staff')}</th>
                  {sessions.map(sess => (
                    <th key={sess.id} style={{ padding: '0.75rem 1rem', fontWeight: '600', textAlign: 'center' }}>{sess.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {patrolStaffList.length === 0 ? (
                  <tr><td colSpan={sessions.length + 1} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.noEligibleStaff')}</td></tr>
                ) : patrolStaffList.map((st, i) => (
                  <tr key={st.initial} style={{ borderBottom: i === patrolStaffList.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{st.initial}</td>
                    {sessions.map(sess => (
                      <td key={sess.id} style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                        <input
                          type="checkbox" disabled={!canManage}
                          checked={(preferences[st.initial] || []).includes(sess.id)}
                          onChange={() => togglePreference(st.initial, sess.id)}
                          style={{ width: '16px', height: '16px', cursor: canManage ? 'pointer' : 'default' }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('patrolDuty.staff')}</th>
                  {sessions.map(sess => (
                    <th key={sess.id} style={{ padding: '0.75rem 1rem', fontWeight: '600', textAlign: 'right' }}>{sess.label}</th>
                  ))}
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '700', textAlign: 'right' }}>{t('patrolDuty.total')}</th>
                </tr>
              </thead>
              <tbody>
                {loadingAssignments ? (
                  <tr><td colSpan={sessions.length + 2} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                ) : patrolStaffList.length === 0 ? (
                  <tr><td colSpan={sessions.length + 2} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>No patrol duty recorded yet for {yearId || 'this year'}.</td></tr>
                ) : patrolStaffList.map((st, i) => {
                  const rowTotal = sessions.reduce((sum, sess) => sum + (statsMatrix[st.initial]?.[sess.id] || 0), 0);
                  return (
                    <tr key={st.initial} style={{ borderBottom: i === patrolStaffList.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{st.initial}</td>
                      {sessions.map(sess => (
                        <td key={sess.id} style={{ padding: '0.75rem 1rem', textAlign: 'right', color: 'var(--color-text-muted)' }}>
                          {(statsMatrix[st.initial]?.[sess.id] || 0) || '-'}
                        </td>
                      ))}
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700' }}>{rowTotal || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {patrolStaffList.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', fontSize: '0.875rem', fontWeight: '700' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>{t('patrolDuty.total')}</td>
                    {sessions.map(sess => {
                      const colTotal = patrolStaffList.reduce((sum, st) => sum + (statsMatrix[st.initial]?.[sess.id] || 0), 0);
                      return <td key={sess.id} style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{colTotal || '-'}</td>;
                    })}
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                      {patrolStaffList.reduce((sum, st) => sum + sessions.reduce((s2, sess) => s2 + (statsMatrix[st.initial]?.[sess.id] || 0), 0), 0) || '-'}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                {t('patrolDuty.readOnlySettings')}
              </div>
            )}

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('patrolDuty.patrolYear')}</h3>
              <select className="input-field" value={yearDraft} disabled={!canManage} onChange={e => setYearDraft(e.target.value)} style={{ width: '200px' }}>
                {academicYears.length === 0 ? <option value="">{t('patrolDuty.noYears')}</option> : academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearId}</option>)}
              </select>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('patrolDuty.patrolSessions')}</h3>
                {canManage && (
                  <button type="button" className="btn-outline" onClick={addSessionRow} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>
                    <Plus size={14} /> Add Session
                  </button>
                )}
              </div>
              {sessionsDraft.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.noSessionsYet')}</div>
              ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('patrolDuty.label')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('patrolDuty.startTime')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('patrolDuty.endTime')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('patrolDuty.equivalentLoad')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsDraft.map(sess => (
                      <tr key={sess.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.4rem 0.75rem' }}>
                          <input type="text" className="input-field" disabled={!canManage} value={sess.label} onChange={e => updateSessionRow(sess.id, 'label', e.target.value)} style={{ width: '160px' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.75rem' }}>
                          <input type="time" className="input-field" disabled={!canManage} value={sess.startTime} onChange={e => updateSessionRow(sess.id, 'startTime', e.target.value)} style={{ width: '120px' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.75rem' }}>
                          <input type="time" className="input-field" disabled={!canManage} value={sess.endTime} onChange={e => updateSessionRow(sess.id, 'endTime', e.target.value)} style={{ width: '120px' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.75rem' }}>
                          <input type="number" className="input-field" disabled={!canManage} value={sess.load} onChange={e => updateSessionRow(sess.id, 'load', e.target.value)} style={{ width: '90px' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.75rem' }}>
                          {canManage && (
                            <button type="button" onClick={() => removeSessionRow(sess.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('patrolDuty.patrolDays')}</h3>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('patrolDuty.extraDays')}</div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <input type="date" className="input-field" value={newExtraDay} onChange={e => setNewExtraDay(e.target.value)} style={{ width: '160px' }} />
                      <button type="button" className="btn-outline" onClick={addExtraDay} style={{ width: 'auto', padding: '0.5rem 0.75rem' }}>{t('patrolDuty.add')}</button>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {extraDaysDraft.length === 0 ? (
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.none')}</span>
                    ) : extraDaysDraft.map(d => (
                      <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.6rem', borderRadius: '999px', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', fontSize: '0.75rem', fontWeight: '600' }}>
                        {d}
                        {canManage && (
                          <button type="button" onClick={() => removeExtraDay(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: 'inherit', padding: 0 }}>
                            <X size={11} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('patrolDuty.excludedDays')}</div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <input type="date" className="input-field" value={newExcludedDay} onChange={e => setNewExcludedDay(e.target.value)} style={{ width: '160px' }} />
                      <button type="button" className="btn-outline" onClick={addExcludedDay} style={{ width: 'auto', padding: '0.5rem 0.75rem' }}>{t('patrolDuty.exclude')}</button>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {excludedDaysDraft.length === 0 ? (
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{t('patrolDuty.none')}</span>
                    ) : excludedDaysDraft.map(d => (
                      <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.6rem', borderRadius: '999px', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', fontSize: '0.75rem', fontWeight: '600' }}>
                        {d}
                        {canManage && (
                          <button type="button" onClick={() => removeExcludedDay(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: 'inherit', padding: 0 }}>
                            <X size={11} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: '1 1 50%', minWidth: 0, backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('patrolDuty.moduleAdmin')}</h3>
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '320px' }}>
                  <thead>
                    <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('patrolDuty.role')}</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('patrolDuty.canEdit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Admin', 'Teacher', 'Staff'].map(role => (
                      <tr key={role} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{role}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={role === 'Admin' || editPermissions.roles.includes(role)}
                            disabled={role === 'Admin' || !canManage}
                            onChange={() => toggleEditRole(role)}
                            style={{ width: '16px', height: '16px', cursor: role === 'Admin' ? 'default' : 'pointer' }}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('patrolDuty.others')}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {t('patrolDuty.namedInAccessControl')}
                      </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ flex: '1 1 50%', minWidth: 0, backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('patrolDuty.patrolEligibility')}</h3>
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '320px' }}>
                  <thead>
                    <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('patrolDuty.role')}</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('patrolDuty.needsPatrol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Admin', 'Teacher', 'Staff'].map(role => (
                      <tr key={role} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{role}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={patrolEligibility.roles.includes(role)}
                            disabled={!canManage}
                            onChange={() => toggleEligibleRole(role)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('patrolDuty.othersInclude')}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          type="button" className="btn-outline" disabled={!canManage}
                          onClick={() => { setIncludeSearch(''); setShowIncludeModal(true); }}
                          style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}
                        >
                          {patrolEligibility.staff.length === 0
                            ? t('patrolDuty.none')
                            : patrolEligibility.staff.map(email => staffList.find(s => s.email === email)?.initial || email).join(', ')}
                        </button>
                      </td>
                    </tr>
                    <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('patrolDuty.othersExclude')}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <button
                          type="button" className="btn-outline" disabled={!canManage}
                          onClick={() => { setExcludeSearch(''); setShowExcludeModal(true); }}
                          style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}
                        >
                          {patrolEligibility.excludeStaff.length === 0
                            ? t('patrolDuty.none')
                            : patrolEligibility.excludeStaff.map(email => staffList.find(s => s.email === email)?.initial || email).join(', ')}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Manage "Others" -- Module Admin staff modal */}
      
      {/* Manage "Others" -- Include for eligibility modal */}
      {showIncludeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '420px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('patrolDuty.includeModalTitle')}</h3>
              <button type="button" onClick={() => setShowIncludeModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <input type="text" className="input-field" placeholder={t('patrolDuty.searchStaff')} value={includeSearch} onChange={e => setIncludeSearch(e.target.value)} style={{ marginBottom: '1rem' }} />
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {staffList.filter(s => `${s.name} ${s.initial}`.toLowerCase().includes(includeSearch.toLowerCase())).map(s => {
                const checked = patrolEligibility.staff.includes(s.email);
                return (
                  <label key={s.email} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.5rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', backgroundColor: checked ? 'var(--color-bg-app)' : 'transparent' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleIncludeStaff(s.email)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                    <span style={{ fontSize: '0.875rem' }}>{s.name} ({s.initial})</span>
                  </label>
                );
              })}
            </div>
            <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => setShowIncludeModal(false)}>{t('patrolDuty.done')}</button>
          </div>
        </div>
      )}

      {/* Manage "Others" -- Exclude from eligibility modal */}
      {showExcludeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '420px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('patrolDuty.excludeModalTitle')}</h3>
              <button type="button" onClick={() => setShowExcludeModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <input type="text" className="input-field" placeholder={t('patrolDuty.searchStaff')} value={excludeSearch} onChange={e => setExcludeSearch(e.target.value)} style={{ marginBottom: '1rem' }} />
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {staffList.filter(s => `${s.name} ${s.initial}`.toLowerCase().includes(excludeSearch.toLowerCase())).map(s => {
                const checked = patrolEligibility.excludeStaff.includes(s.email);
                return (
                  <label key={s.email} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.5rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', backgroundColor: checked ? 'var(--color-bg-app)' : 'transparent' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleExcludeStaff(s.email)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                    <span style={{ fontSize: '0.875rem' }}>{s.name} ({s.initial})</span>
                  </label>
                );
              })}
            </div>
            <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => setShowExcludeModal(false)}>{t('patrolDuty.done')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatrolDuty;
